import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { useWindowDimensions } from 'react-native';

import ConfirmDialog from 'components/confirm-dialog';
import Modal from 'components/modal';
import { useOrderStatus } from 'hooks/order-status';
import { useCart } from 'hooks/use-cart';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { useDismissAllAndNavigate } from 'navigation/hooks/use-dismiss-and-navigate';
import { useOrderPayment } from 'pages/cart/payment/order-payment/use-order-payment';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderInjectionFlowVariations } from 'state/launchdarkly/variations';
import { OrderStatus } from 'state/order';
import { routes } from 'utils/routing';

import { Animation } from '../animations';

import {
  BodyComponent,
  BottomLayer,
  Card,
  HeaderComponent,
  Message,
  ModalContainer,
  TopLayer,
} from './styled';

export const PaymentProcessLoading = ({
  rbiOrderId,
  onFinished,
}: {
  rbiOrderId?: string;
  onFinished?: () => void;
}) => {
  const { navigateToOrderConfirmation } = useOrderPayment();
  const isDesktop = useIsDesktopBreakpoint();
  const { height, width } = useWindowDimensions();
  const { formatMessage } = useIntl();
  const { emptyCart } = useCart();
  const [finished, setFinished] = useState<boolean>(false);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [redirected, setRedirected] = useState(false);
  const dismissAndNavigate = useDismissAllAndNavigate();

  const { orderStatus } = useOrderStatus({ rbiOrderId, skip: false, disableCache: true });

  const orderInjectFlow = useFlag(LaunchDarklyFlag.ORDER_INJECTION_FLOW);
  const showImHereFlow = orderInjectFlow === OrderInjectionFlowVariations.IM_HERE;

  useEffect(() => {
    if (orderStatus === OrderStatus.PRICE_SUCCESSFUL && showImHereFlow) {
      navigateToOrderConfirmation();
    }
    if (orderStatus === OrderStatus.INSERT_SUCCESSFUL && !finished) {
      setFinished(true);
      setTimeout(() => {
        if (onFinished) {
          onFinished();
        }
      }, 2000);
      if (isDesktop && !redirected) {
        // this is to allow to show the success message before redirect to order confirmation
        setTimeout(() => navigateToOrderConfirmation(), 1000);
        // this is needed to avoid calling the redirect and activity creation more than one time
        setRedirected(true);
      }
    }
  }, [
    navigateToOrderConfirmation,
    finished,
    orderStatus,
    isDesktop,
    rbiOrderId,
    showImHereFlow,
    redirected,
    onFinished,
  ]);

  const openConfirmationModal = () => {
    setModalOpen(true);
  };

  const message = finished
    ? formatMessage({ id: 'cartCardLoadingSuccess' })
    : formatMessage({ id: 'cartCardLoadingMessage' });

  return (
    <>
      <Modal
        hideStatusBar={true}
        amplitudeEventData={{ modalAppearanceEventMessage: 'Processing Order Modal' }}
        contentStyle={{ height: '100%' }}
        scrollViewBounces={false}
        onDismiss={openConfirmationModal}
        isFullHeightContent
      >
        <ModalContainer height={height}>
          <BottomLayer width={width} height={height}>
            <Animation
              height={height}
              width={width}
              finished={finished}
              isDesktop={isDesktop}
              rbiOrderId={rbiOrderId}
            />
            {isDesktop && <Message>{message}</Message>}
          </BottomLayer>
          {!isDesktop && (
            <TopLayer top={height * -0.55}>
              <Card>
                <Message>{message}</Message>
              </Card>
            </TopLayer>
          )}
          {isModalOpen && (
            <ConfirmDialog
              showDialog={true}
              headingComponent={
                <HeaderComponent>
                  {formatMessage({ id: 'cartCloseConfirmDialogHeading' })}
                </HeaderComponent>
              }
              bodyComponent={
                <BodyComponent>{formatMessage({ id: 'cartConfirmDialogBody' })}</BodyComponent>
              }
              cancelLabel={formatMessage({ id: 'close' })}
              confirmLabel={formatMessage({ id: 'cartConfirmDialogConfirmLabel' })}
              onDismiss={() => setModalOpen(false)}
              onCancel={() => setModalOpen(false)}
              onConfirm={() => {
                emptyCart();
                dismissAndNavigate(routes.base);
              }}
              modalAppearanceEventMessage="Confirm Close Cart Animation"
              style={{ alignContent: 'center' }}
            />
          )}
        </ModalContainer>
      </Modal>
    </>
  );
};
